
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Leaf, Target, TrendingUp, Award } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/Auth/AuthProvider';

export const PerformanceMetrics = () => {
  const [metrics, setMetrics] = useState({
    carbonFootprint: 0,
    esgScore: 0,
    rating: 'Not Rated',
    performanceIndex: 0,
    netZeroProgress: 0,
    riskScore: 0
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchMetrics();
    }
  }, [user]);

  const fetchMetrics = async () => {
    try {
      // Fetch carbon footprint from calculations
      const { data: carbonData } = await supabase
        .from('carbon_calculations')
        .select('co2_equivalent')
        .eq('user_id', user?.id);

      // Fetch ESG data points from unified library
      const { data: esgData } = await supabase
        .from('user_metrics_data')
        .select('custom_value, name')
        .eq('organization_id', user?.id);

      // Fetch net-zero targets progress
      const { data: targetData } = await supabase
        .from('net_zero_targets')
        .select('progress_percentage')
        .eq('user_id', user?.id)
        .eq('status', 'active');

      // Fetch risk assessments
      const { data: riskData } = await supabase
        .from('risk_assessments')
        .select('risk_score')
        .eq('user_id', user?.id)
        .eq('status', 'active');

      // Calculate metrics
      const totalCarbon = carbonData?.reduce((sum, item) => sum + (item.co2_equivalent || 0), 0) || 0;
      const avgESGScore = esgData && esgData.length > 0 
        ? esgData.reduce((sum, item) => sum + (item.custom_value || 0), 0) / esgData.length 
        : 0;
      const avgProgress = targetData && targetData.length > 0
        ? targetData.reduce((sum, item) => sum + (item.progress_percentage || 0), 0) / targetData.length
        : 0;
      const avgRiskScore = riskData && riskData.length > 0
        ? riskData.reduce((sum, item) => sum + (item.risk_score || 0), 0) / riskData.length
        : 0;

      // Calculate performance index (weighted average of key metrics)
      const performanceIndex = avgESGScore > 0 ? 
        Math.round((avgESGScore * 0.4) + (avgProgress * 0.3) + ((100 - avgRiskScore) * 0.3)) : 0;

      // Determine ESG rating
      let rating = 'Not Rated';
      if (avgESGScore >= 90) rating = 'AAA';
      else if (avgESGScore >= 80) rating = 'AA';
      else if (avgESGScore >= 70) rating = 'A';
      else if (avgESGScore >= 60) rating = 'BBB';
      else if (avgESGScore >= 50) rating = 'BB';
      else if (avgESGScore > 0) rating = 'B';

      setMetrics({
        carbonFootprint: Math.round(totalCarbon),
        esgScore: Math.round(avgESGScore),
        rating,
        performanceIndex,
        netZeroProgress: Math.round(avgProgress),
        riskScore: Math.round(avgRiskScore)
      });
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'AAA': return 'bg-green-600';
      case 'AA': return 'bg-green-500';
      case 'A': return 'bg-green-400';
      case 'BBB': return 'bg-yellow-500';
      case 'BB': return 'bg-orange-500';
      case 'B': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getPerformanceColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-8 bg-gray-300 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Carbon Footprint */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Carbon Footprint</CardTitle>
            <Leaf className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.carbonFootprint.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">tCO₂e total emissions</p>
          </CardContent>
        </Card>

        {/* ESG Score */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ESG Score</CardTitle>
            <Target className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.esgScore}/100</div>
            <Progress value={metrics.esgScore} className="mt-2" />
          </CardContent>
        </Card>

        {/* ESG Rating */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ESG Rating</CardTitle>
            <Award className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <Badge className={`text-lg font-bold px-3 py-1 ${getRatingColor(metrics.rating)} text-white`}>
              {metrics.rating}
            </Badge>
            <p className="text-xs text-muted-foreground mt-2">Current sustainability rating</p>
          </CardContent>
        </Card>

        {/* Performance Index */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Performance Index</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getPerformanceColor(metrics.performanceIndex)}`}>
              {metrics.performanceIndex}/100
            </div>
            <Progress value={metrics.performanceIndex} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Additional Performance Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Net-Zero Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Target Achievement</span>
                <span>{metrics.netZeroProgress}%</span>
              </div>
              <Progress value={metrics.netZeroProgress} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Risk Assessment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Average Risk Score</span>
                <span className={metrics.riskScore > 15 ? 'text-red-600' : metrics.riskScore > 10 ? 'text-yellow-600' : 'text-green-600'}>
                  {metrics.riskScore}/25
                </span>
              </div>
              <Progress value={(metrics.riskScore / 25) * 100} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
